import { Input, Select } from '@/components/ui/FormControls';
import StockEntry from '@/components/inventory/StockEntry';
import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Inventory Page (grouped + filtered)
// TASK-FE-INV-001: Grouped product → variant expandable hierarchy
// TASK-FE-INV-002: Size / Team / Product Type filters + search debounce
// ─────────────────────────────────────────────

import { useState, useMemo, useEffect, useCallback, Fragment } from 'react';
import { useInventory, InventorySummaryRow } from '@/hooks/useInventory';
import { useProductTypes } from '@/hooks/useMasterConfig';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { SkeletonTable } from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import ExportActions from '@/components/common/ExportActions';
import AdjustStockModal from '@/components/inventory/AdjustStockModal';
import { Warehouse, Search, PlusCircle, SlidersHorizontal, ChevronRight, X, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

type StockFilter = '' | 'in_stock' | 'low_stock' | 'out_of_stock';

// ── Types ──────────────────────────────────────────────────────
interface ProductGroup {
  product_id: string;
  product_name: string;
  product_code: string;
  team: string | null;
  product_type_name: string | null;
  product_type_id?: string | null;
  totalOnHand: number;
  totalAvailable: number;
  variantCount: number;
  overallStatus: StockFilter;
  variants: InventorySummaryRow[];
}

// ── Status chip config ─────────────────────────────────────────
const STATUS_CHIPS: { label: string; value: StockFilter; dotColor: string; activeClass: string }[] = [
  { label: 'All', value: '', dotColor: 'bg-muted-foreground', activeClass: 'ring-2 ring-foreground/20 bg-card text-foreground' },
  { label: 'In Stock', value: 'in_stock', dotColor: 'bg-emerald-500', activeClass: 'ring-2 ring-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  { label: 'Low Stock', value: 'low_stock', dotColor: 'bg-amber-500', activeClass: 'ring-2 ring-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  { label: 'Out of Stock', value: 'out_of_stock', dotColor: 'bg-destructive', activeClass: 'ring-2 ring-destructive bg-destructive/10 text-destructive' },
];

// ── Compute overall stock status for a product group ───────────
function computeGroupStatus(variants: InventorySummaryRow[]): StockFilter {
  const totalOnHand = variants.reduce((s, v) => s + (v.quantity_on_hand ?? 0), 0);
  if (totalOnHand === 0) return 'out_of_stock';
  if (variants.some((v) => v.stock_status === 'low_stock')) return 'low_stock';
  return 'in_stock';
}

export default function InventoryPage() {
  // ── Search — debounced ─────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Filter state ───────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StockFilter>('');
  const [filterProductType, setFilterProductType] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterSize, setFilterSize] = useState('');

  // ── Data ───────────────────────────────────────────────────
  const { data: items, isLoading, error, refetch } = useInventory(search);
  const { data: productTypes } = useProductTypes();
  const [adjustingItem, setAdjustingItem] = useState<InventorySummaryRow | null>(null);

  const categoryOptions = useMemo(() => {
    const actual = new Map<string, {id: string; name: string; code: string}>();
    for (const item of items ?? []) {
      if (item.product_type_id && item.product_type_name) actual.set(item.product_type_id, {id: item.product_type_id, name: item.product_type_name, code: ''});
    }
    return actual.size ? [...actual.values()] : productTypes;
  }, [items, productTypes]);

  // ── Expanded product rows ──────────────────────────────────
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Derived filter option lists (client-side, no extra API) ─
  const teamOptions = useMemo(() =>
    [...new Set((items ?? []).map((r) => r.team).filter(Boolean) as string[])].sort(),
    [items]
  );

  const sizeOptions = useMemo(() =>
    [...new Set((items ?? []).map((r) => r.size).filter(Boolean) as string[])].sort(),
    [items]
  );

  // ── Group + filter ─────────────────────────────────────────
  const groupedProducts = useMemo<ProductGroup[]>(() => {
    if (!items) return [];

    // 1. Apply flat variant-level filters first
    const filtered = items.filter((row) => {
      if (statusFilter && row.stock_status !== statusFilter) return false;
      if (filterTeam && row.team !== filterTeam) return false;
      if (filterSize && row.size !== filterSize) return false;
      return true;
    });

    // 2. Group into product buckets
    const map = new Map<string, ProductGroup>();
    for (const row of filtered) {
      const existing = map.get(row.product_id);
      if (existing) {
        existing.variants.push(row);
        existing.totalOnHand += row.quantity_on_hand ?? 0;
        existing.totalAvailable += row.quantity_available ?? 0;
        existing.variantCount += 1;
      } else {
        map.set(row.product_id, {
          product_id: row.product_id,
          product_name: row.product_name,
          product_code: row.product_code,
          team: row.team ?? null,
          product_type_name: row.product_type_name ?? null,
          product_type_id: row.product_type_id,
          totalOnHand: row.quantity_on_hand ?? 0,
          totalAvailable: row.quantity_available ?? 0,
          variantCount: 1,
          overallStatus: 'in_stock', // placeholder; computed below
          variants: [row],
        });
      }
    }

    // 3. Compute overall status + apply product-type filter
    const result: ProductGroup[] = [];
    for (const group of map.values()) {
      group.overallStatus = computeGroupStatus(group.variants);
      // Product type filter: match by name if available, else skip filter
      if (filterProductType) {
        const ptMatch = categoryOptions?.find((pt: any) => (pt.id || pt.code) === filterProductType);
        const ptName = ptMatch?.name?.toLowerCase() ?? '';
        const groupTypeName = (group.product_type_name ?? '').toLowerCase();
        if (group.product_type_id !== undefined) {
          if (group.product_type_id !== filterProductType) continue;
        } else if (ptName && groupTypeName !== ptName) continue;
      }
      result.push(group);
    }

    return result;
  }, [items, statusFilter, filterTeam, filterSize, filterProductType, categoryOptions]);

  // ── Status counts (on raw items, not filtered) ─────────────
  const counts = useMemo(() => ({
    in_stock: items?.filter((i) => i.stock_status === 'in_stock').length ?? 0,
    low_stock: items?.filter((i) => i.stock_status === 'low_stock').length ?? 0,
    out_of_stock: items?.filter((i) => i.stock_status === 'out_of_stock').length ?? 0,
    total: items?.length ?? 0,
  }), [items]);

  const hasActiveFilters = !!(filterProductType || filterTeam || filterSize);
  const clearAllFilters = () => {
    setFilterProductType('');
    setFilterTeam('');
    setFilterSize('');
    setStatusFilter('');
  };

  const filterSummaryText = [
    searchInput ? `Search: "${searchInput}"` : null,
    statusFilter ? `Status: ${statusFilter.replace('_', ' ')}` : null,
    filterTeam ? `Team: ${filterTeam}` : null,
    filterSize ? `Size: ${filterSize}` : null,
    filterProductType ? `Type: ${productTypes?.find((pt: any) => (pt.id || pt.code) === filterProductType)?.name ?? filterProductType}` : null,
  ].filter(Boolean).join(' | ') || 'All Inventory';

  // ── Flat list for export (all visible variant rows) ────────
  const exportRows = useMemo(() =>
    groupedProducts.flatMap((g) => g.variants),
    [groupedProducts]
  );

  const exportInventoryData = useMemo(() =>
    exportRows.map((i) => ({
      ...i,
      sku: i.sku || '—',
      product_name: i.product_name || '—',
      size: i.size || '—',
      color: i.color || '—',
      stock_qty: Number(i.quantity_available ?? i.quantity_on_hand ?? 0),
      cost_price: Number(i.weighted_avg_cost ?? 0),
      stock_value: Number((i.quantity_available ?? i.quantity_on_hand ?? 0) * Number(i.weighted_avg_cost ?? 0)),
    })),
    [exportRows]
  );

  return (
    <div>
      <PageHeader
        title="Inventory"
        description="Stock levels and cost basis for all variants"
        actions={
          <ExportActions
            exportType="inventory"
            data={exportInventoryData as unknown as Record<string, unknown>[]}
            excelOptions={() => ({
              fileName: `Inventory_Report_${new Date().toISOString().split('T')[0]}`,
              sheetName: 'Inventory',
              filtersSummary: filterSummaryText,
              headers: ['Product', 'Code', 'SKU', 'Size/Color', 'On Hand', 'Reserved', 'Available', 'WAC (₹)', 'Est. Stock Value (₹)', 'Status'],
              data: exportRows.map((i) => [
                i.product_name,
                i.product_code,
                i.sku,
                `${i.size}${i.color ? '/' + i.color : ''}`,
                i.quantity_on_hand,
                i.quantity_reserved,
                i.quantity_available,
                Number(i.weighted_avg_cost),
                Number(i.quantity_available * Number(i.weighted_avg_cost)),
                i.stock_status,
              ]),
            })}
          />
        }
      />

      <div className="mb-4 flex justify-end"><StockEntry /></div>
      {/* ── Search + Status chips ───────────────────────────────── */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Search */}
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            id="inventory-search"
            placeholder="Search by product, SKU…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
            aria-label="Search inventory"
          />
        </div>

        {/* Status chips */}
        {items && items.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {STATUS_CHIPS.map((chip) => {
              const count = chip.value === '' ? counts.total
                : chip.value === 'in_stock' ? counts.in_stock
                : chip.value === 'low_stock' ? counts.low_stock
                : counts.out_of_stock;
              const isActive = statusFilter === chip.value;
              return (
                <button
                  key={chip.value}
                  onClick={() => setStatusFilter(chip.value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-xs font-medium transition-all duration-150',
                    isActive ? chip.activeClass : 'bg-card text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                  title={`Filter: ${chip.label}`}
                >
                  <span className={cn('h-1.5 w-1.5 rounded-full', chip.dotColor)} />
                  {chip.label}
                  {chip.value !== '' && (
                    <span className="ml-0.5 font-bold">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Additional filters row ──────────────────────────────── */}
      {items && items.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

          {/* Product Type */}
          <div className="flex items-center gap-1.5">
            <label htmlFor="inv-filter-type" className="text-xs text-muted-foreground sr-only">Product Type</label>
            <Select
              id="inv-filter-type"
              value={filterProductType}
              onChange={(e) => setFilterProductType(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Filter by product type"
            >
              <option value="">All Types</option>
              {(productTypes ?? []).map((pt: any) => (
                <option key={pt.id || pt.code} value={pt.id || pt.code}>{pt.name}</option>
              ))}
            </Select>
          </div>

          {/* Team */}
          {teamOptions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <label htmlFor="inv-filter-team" className="text-xs text-muted-foreground sr-only">Team</label>
              <Select
                id="inv-filter-team"
                value={filterTeam}
                onChange={(e) => setFilterTeam(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Filter by team"
              >
                <option value="">All Teams</option>
                {teamOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </div>
          )}

          {/* Size */}
          {sizeOptions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <label htmlFor="inv-filter-size" className="text-xs text-muted-foreground sr-only">Size</label>
              <Select
                id="inv-filter-size"
                value={filterSize}
                onChange={(e) => setFilterSize(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Filter by size"
              >
                <option value="">All Sizes</option>
                {sizeOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </div>
          )}
        </div>
      )}

      {/* ── Active filter chips ─────────────────────────────────── */}
      {hasActiveFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2" role="status" aria-label="Active filters">
          {filterProductType && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-xs font-medium text-primary">
              Type: {productTypes?.find((pt: any) => (pt.id || pt.code) === filterProductType)?.name ?? filterProductType}
              <button
                type="button"
                onClick={() => setFilterProductType('')}
                aria-label="Remove product type filter"
                className="ml-0.5 rounded-full hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filterTeam && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-xs font-medium text-primary">
              Team: {filterTeam}
              <button
                type="button"
                onClick={() => setFilterTeam('')}
                aria-label="Remove team filter"
                className="ml-0.5 rounded-full hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filterSize && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-xs font-medium text-primary">
              Size: {filterSize}
              <button
                type="button"
                onClick={() => setFilterSize('')}
                aria-label="Remove size filter"
                className="ml-0.5 rounded-full hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* ── Loading / Error / Empty states ─────────────────────── */}
      {isLoading && <SkeletonTable rows={8} />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && items?.length === 0 && (
        <EmptyState
          icon={<Warehouse className="h-12 w-12" />}
          title="No inventory data yet"
          description="Stock will appear here after products are created and purchases are received"
        />
      )}

      {!isLoading && !error && items && items.length > 0 && groupedProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Warehouse className="h-12 w-12 text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No products match your filters</p>
          <button
            type="button"
            onClick={clearAllFilters}
            className="mt-3 text-xs text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* ── Grouped inventory table ─────────────────────────────── */}
      {!isLoading && !error && groupedProducts.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-border bg-card shadow-sm md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="Inventory grouped by product">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="w-8 px-2 py-3" aria-label="Expand" />
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Product</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Team</th>
                    <th className="px-4 py-3 text-center font-medium text-muted-foreground">Variants</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">On Hand</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">Available</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                    <th className="w-24 px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {groupedProducts.map((group) => {
                    const isExpanded = expandedIds.has(group.product_id);
                    return (
                      <Fragment key={`group-${group.product_id}`}>
                        {/* Product group row */}
                        <tr
                          className="transition-colors hover:bg-muted/30 cursor-pointer"
                          onClick={() => toggleExpand(group.product_id)}
                        >
                          <td className="px-2 py-3 text-center">
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? `Collapse ${group.product_name}` : `Expand ${group.product_name}`}
                              className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(group.product_id); }}
                            >
                              <ChevronRight className={cn('h-4 w-4 transition-transform duration-150', isExpanded && 'rotate-90')} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-foreground">{group.product_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{group.product_code}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{group.team ?? '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                              {group.variantCount} {group.variantCount === 1 ? 'variant' : 'variants'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{group.totalOnHand}</td>
                          <td className={cn(
                            'px-4 py-3 text-right font-semibold',
                            group.totalAvailable <= 0 ? 'text-destructive'
                              : group.overallStatus === 'low_stock' ? 'text-amber-600 dark:text-amber-400'
                              : 'text-foreground',
                          )}>
                            {group.totalAvailable}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={group.overallStatus || 'out_of_stock'} size="sm" />
                          </td>
                          {/* Actions — empty for product row; Adjust lives on variant rows */}
                          <td className="px-4 py-3" />
                        </tr>

                        {/* Variant sub-rows */}
                        {isExpanded && group.variants.map((item) => (
                          <tr
                            key={`variant-${item.variant_id}`}
                            className="bg-muted/20 transition-colors hover:bg-muted/40"
                          >
                            {/* Expand spacer */}
                            <td className="px-2 py-2" />

                            {/* Variant name + SKU — indented under Product column */}
                            <td className="px-4 py-2">
                              <div className="flex items-start gap-2 pl-4">
                                <span className="mt-0.5 text-muted-foreground/30 select-none text-xs leading-none">└───</span>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-foreground">
                                    {item.size}{item.color ? ` / ${item.color}` : ''}
                                  </p>
                                  <p className="mt-0.5 max-w-[260px] break-all text-[11px] font-mono text-muted-foreground" title={item.sku}>
                                    {item.sku}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Team */}
                            <td className="px-4 py-2 text-xs text-muted-foreground">{item.team ?? '—'}</td>

                            {/* Variants — empty for sub-rows */}
                            <td className="px-4 py-2 text-center text-xs text-muted-foreground">—</td>

                            {/* On Hand */}
                            <td className="px-4 py-2 text-right text-sm font-medium">{item.quantity_on_hand}</td>

                            {/* Available */}
                            <td className={cn(
                              'px-4 py-2 text-right text-sm font-semibold',
                              item.quantity_available <= 0 ? 'text-destructive'
                                : item.stock_status === 'low_stock' ? 'text-amber-600 dark:text-amber-400'
                                : 'text-foreground',
                            )}>
                              {item.quantity_available}
                            </td>

                            {/* Status — badge only, no button */}
                            <td className="px-4 py-2">
                              <StatusBadge status={item.stock_status} size="sm" />
                            </td>

                            {/* Actions — Adjust button in its own column */}
                            <td className="px-4 py-2 text-right">
                              <PermissionGate permission="inventory.adjust"><button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setAdjustingItem(item); }}
                                className="inline-flex items-center gap-1 rounded border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent hover:border-primary/30 transition-colors"
                                title="Adjust Stock"
                                aria-label={`Adjust stock for ${item.sku}`}
                              >
                                <PlusCircle className="h-3.5 w-3.5 text-emerald-600" />
                                Adjust
                              </button></PermissionGate>
                            </td>
                          </tr>
                        ))}

                        {/* Empty variants fallback */}
                        {isExpanded && group.variants.length === 0 && (
                          <tr key={`empty-${group.product_id}`} className="bg-muted/20">
                            <td colSpan={8} className="px-8 py-3 text-xs text-muted-foreground italic">No stock records found for this product.</td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards — grouped */}
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm md:hidden">
            {groupedProducts.map((group) => {
              const isExpanded = expandedIds.has(group.product_id);
              return (
                <div key={`mobile-group-${group.product_id}`}>
                  {/* Product header card */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(group.product_id)}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? `Collapse ${group.product_name}` : `Expand ${group.product_name}`}
                    className="flex w-full items-start justify-between px-4 py-3 gap-3 hover:bg-muted/30 transition-colors text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground text-sm">{group.product_name}</p>
                      <p className="mt-0.5 text-xs font-mono text-muted-foreground">{group.product_code}</p>
                      {group.team && <p className="mt-0.5 text-xs text-muted-foreground">{group.team}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={cn(
                        'text-sm font-semibold',
                        group.totalAvailable <= 0 ? 'text-destructive' : 'text-foreground'
                      )}>
                        {group.totalAvailable} avail
                      </span>
                      <StatusBadge status={group.overallStatus || 'out_of_stock'} size="sm" />
                      <span className="text-[10px] text-muted-foreground">{group.variantCount} variant{group.variantCount !== 1 ? 's' : ''}</span>
                      <ChevronRight className={cn('h-4 w-4 text-muted-foreground transition-transform duration-150', isExpanded && 'rotate-90')} />
                    </div>
                  </button>

                  {/* Variant sub-cards */}
                  {isExpanded && (
                    <div className="divide-y divide-border/50 bg-muted/10">
                      {group.variants.length === 0 && (
                        <p className="px-6 py-3 text-xs text-muted-foreground italic">No stock records found.</p>
                      )}
                      {group.variants.map((item) => (
                        <div key={`mobile-variant-${item.variant_id}`} className="flex items-start justify-between px-6 py-2.5 gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-foreground">
                              {item.size}{item.color ? ` / ${item.color}` : ''}
                            </p>
                            <p className="mt-0.5 break-all text-[11px] font-mono text-muted-foreground" title={item.sku}>
                              {item.sku}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={cn(
                              'text-xs font-semibold',
                              item.quantity_available <= 0 ? 'text-destructive' : 'text-foreground',
                            )}>
                              {item.quantity_available} avail
                            </span>
                            <StatusBadge status={item.stock_status} size="sm" />
                            <PermissionGate permission="inventory.adjust"><button
                              type="button"
                              onClick={() => setAdjustingItem(item)}
                              className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-[10px] font-medium hover:bg-accent transition-colors mt-0.5"
                              title="Adjust Stock"
                              aria-label={`Adjust stock for ${item.sku}`}
                            >
                              <PlusCircle className="h-3 w-3 text-emerald-600" />
                              Adjust
                            </button></PermissionGate>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {groupedProducts.length} product{groupedProducts.length !== 1 ? 's' : ''} · {exportRows.length} variant{exportRows.length !== 1 ? 's' : ''}
            {hasActiveFilters || statusFilter ? ' · filters active' : ''}
          </p>
        </>
      )}

      {/* Adjust Stock Modal */}
      {adjustingItem && (
        <AdjustStockModal
          isOpen={!!adjustingItem}
          onClose={() => setAdjustingItem(null)}
          variantId={adjustingItem.variant_id}
          sku={adjustingItem.sku}
          productName={adjustingItem.product_name}
          currentOnHand={adjustingItem.quantity_on_hand}
          unitCost={Number(adjustingItem.weighted_avg_cost ?? 0)}
        />
      )}
    </div>
  );
}
